import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  Divider,
  SelectChangeEvent,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { useFormik } from "formik";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import {
  fetchSponsoredProducts,
  addSponsoredProduct,
  toggleProductSponsorship,
  deleteSponsoredProduct,
} from "../../../State/admin/adminBannerSlice";
import { fetchAllAdminProducts } from "../../../State/admin/adminSlice";
import { fetchSellerProducts } from "../../../State/seller/sellerProductSlice";
import api from "../../../config/Api";

interface Category {
  id?: number | string;
  categoryId?: number | string;
  name?: string;
  categoryName?: string;
  level?: number;
  parentCategory?: Category;
  parentCategoryId?: number | string;
  levelTwoCategories?: Category[];
  levelThreeCategories?: Category[];
  children?: Category[];
}

interface Product {
  id: number;
  title: string;
  sellingPrice?: number;
  mrpPrice?: number;
  image?: string;
  images?: string[];
  seller?: { id: number; sellerName?: string; businessDetails?: { businessName?: string } };
  sellerId?: number;
  category?: Category;
  sponsored?: boolean;
  isSponsored?: boolean;
  sponsoredValidTill?: string;
}

interface SponsoredProductRow {
  id: number; // This is the SponsoredProduct record ID
  active: boolean;
  validTill?: string;
  product: Product;
}

const getId = (obj: Category | string | number | undefined | null): string => {
  if (!obj) return "";
  if (typeof obj === "string" || typeof obj === "number") return String(obj);
  return String(obj.categoryId || obj.id || "");
};

const SponsoredProductsTable: React.FC = () => {
  const dispatch = useAppDispatch();

  const adminBannerState = useAppSelector((store: any) => store.homeBanner);
  const adminState = useAppSelector((store: any) => store.admin);
  const sellerProductState = useAppSelector((store: any) => store.sellerProduct);

  const [sellers, setSellers] = useState<any[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [mainCategories, setMainCategories] = useState<Category[]>([]);
  const [subCategories, setSubCategories] = useState<Category[]>([]);
  const [itemCategories, setItemCategories] = useState<Category[]>([]);

  const [formError, setFormError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Correctly reading sponsored products from adminBanner slice state
  const sponsoredProducts = useMemo(
    () => adminBannerState?.sponsoredProducts || [],
    [adminBannerState]
  );

  const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);

  useEffect(() => {
    dispatch(fetchSponsoredProducts());
    dispatch(fetchAllAdminProducts());

    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem("jwt");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [sellersRes, categoriesRes] = await Promise.all([
          api.get("/sellers", { headers }),
          api.get("/categories", { headers }),
        ]);

        const rawCategories: Category[] = categoriesRes.data || [];
        setSellers(sellersRes.data || []);
        setAllCategories(rawCategories);

        const level1 = rawCategories.filter((c) => {
          const parentId = getId(c.parentCategory) || getId(c.parentCategoryId);
          return Number(c.level) === 1 || (!parentId && !c.parentCategory);
        });
        setMainCategories(level1);
      } catch (err) {
        console.error("Failed to load initial sellers or categories:", err);
      }
    };

    fetchInitialData();
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      sellerId: "",
      mainCategory: "",
      subCategory: "",
      itemCategory: "",
      productId: "",
      validTill: "",
    },
    onSubmit: async (values, { resetForm }) => {
      setFormError(null);
      setSuccessMsg(null);

      if (!values.productId) {
        setFormError("Please select a product to sponsor.");
        return;
      }
      if (!values.validTill) {
        setFormError("Please choose a 'Valid Till' date.");
        return;
      }

      setIsSubmitting(true);
      try {
        const formattedValidTill = values.validTill.includes("T")
          ? values.validTill
          : `${values.validTill}T23:59:59`;

        await dispatch(
          addSponsoredProduct({
            productId: Number(values.productId),
            validTill: formattedValidTill,
          })
        ).unwrap();

        setSuccessMsg("Product sponsored successfully!");
        resetForm();
        dispatch(fetchSponsoredProducts());
      } catch (error: any) {
        console.error("Failed to add sponsored product:", error);
        setFormError(
          typeof error === "string"
            ? error
            : error?.message || "Failed to add sponsored product"
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const allAvailableProducts: Product[] = useMemo(() => {
    if (formik.values.sellerId && sellerProductState?.products?.length) {
      return sellerProductState.products;
    }
    return adminState?.products?.length
      ? adminState.products
      : sellerProductState?.products || [];
  }, [formik.values.sellerId, adminState?.products, sellerProductState?.products]);

  const filteredProducts = useMemo(() => {
    let result = [...allAvailableProducts];

    if (formik.values.sellerId) {
      result = result.filter((p) => {
        const pSellerId = getId(p.seller) || String(p.sellerId || "");
        return pSellerId === String(formik.values.sellerId);
      });
    }

    const targetCatId =
      formik.values.itemCategory ||
      formik.values.subCategory ||
      formik.values.mainCategory;

    if (targetCatId) {
      result = result.filter((p) => {
        const cat = p.category;
        if (!cat) return false;

        const pCatId = getId(cat);
        const pParentId = getId(cat.parentCategory) || getId(cat.parentCategoryId);
        const pGrandParentId = getId(cat.parentCategory?.parentCategory);

        return (
          pCatId === String(targetCatId) ||
          pParentId === String(targetCatId) ||
          pGrandParentId === String(targetCatId)
        );
      });
    }

    return result;
  }, [
    allAvailableProducts,
    formik.values.sellerId,
    formik.values.mainCategory,
    formik.values.subCategory,
    formik.values.itemCategory,
  ]);

  const handleMainCategoryChange = (e: SelectChangeEvent<string>) => {
    const mainCatVal = String(e.target.value);
    formik.setFieldValue("mainCategory", mainCatVal);
    formik.setFieldValue("subCategory", "");
    formik.setFieldValue("itemCategory", "");
    formik.setFieldValue("productId", "");
    setSubCategories([]);
    setItemCategories([]);

    if (mainCatVal) {
      const selectedMain = allCategories.find((c) => getId(c) === mainCatVal);
      let subCats = selectedMain?.levelTwoCategories || selectedMain?.children || [];

      if (!subCats.length) {
        subCats = allCategories.filter((c) => {
          const parentId = getId(c.parentCategory) || getId(c.parentCategoryId);
          return parentId === mainCatVal;
        });
      }

      setSubCategories(subCats);
    }
  };

  const handleSubCategoryChange = (e: SelectChangeEvent<string>) => {
    const subCatVal = String(e.target.value);
    formik.setFieldValue("subCategory", subCatVal);
    formik.setFieldValue("itemCategory", "");
    formik.setFieldValue("productId", "");
    setItemCategories([]);

    if (subCatVal) {
      const selectedSub =
        subCategories.find((sc) => getId(sc) === subCatVal) ||
        allCategories.find((c) => getId(c) === subCatVal);

      let itemCats = selectedSub?.levelThreeCategories || selectedSub?.children || [];

      if (!itemCats.length) {
        itemCats = allCategories.filter((c) => {
          const parentId = getId(c.parentCategory) || getId(c.parentCategoryId);
          return parentId === subCatVal;
        });
      }

      setItemCategories(itemCats);
    }
  };

  const handleItemCategoryChange = (e: SelectChangeEvent<string>) => {
    formik.setFieldValue("itemCategory", String(e.target.value));
    formik.setFieldValue("productId", "");
  };

  const handleSellerChange = (e: SelectChangeEvent<string>) => {
    const sellerId = String(e.target.value);
    formik.setFieldValue("sellerId", sellerId);
    formik.setFieldValue("productId", "");

    if (sellerId) {
      const jwt = localStorage.getItem("jwt") || "";
      dispatch(fetchSellerProducts({ jwt, sellerId }));
    }
  };

  const handleToggleSponsor = useCallback(
    async (sponsoredRecordId: number) => {
      await dispatch(toggleProductSponsorship(sponsoredRecordId));
      dispatch(fetchSponsoredProducts());
    },
    [dispatch]
  );

  const handleDeleteSponsor = useCallback(
    async (sponsoredRecordId: number) => {
      if (window.confirm("Are you sure you want to remove this product from sponsored items?")) {
        await dispatch(deleteSponsoredProduct(sponsoredRecordId));
        dispatch(fetchSponsoredProducts());
      }
    },
    [dispatch]
  );

  return (
    <div className="space-y-6 max-w-5xl mx-auto p-4">
      <h1 className="text-2xl font-bold text-gray-800">Manage Sponsored Products</h1>

      <Box
        component="form"
        noValidate
        onSubmit={formik.handleSubmit}
        className="space-y-4 p-6 bg-white rounded-lg shadow-md border border-gray-200"
      >
        <h2 className="text-lg font-semibold text-gray-700">Add Sponsored Product</h2>

        {formError && <Alert severity="error">{formError}</Alert>}
        {successMsg && <Alert severity="success">{successMsg}</Alert>}

        <FormControl fullWidth size="small">
          <InputLabel id="seller-select-label">Seller</InputLabel>
          <Select
            labelId="seller-select-label"
            name="sellerId"
            value={formik.values.sellerId}
            label="Seller"
            onChange={handleSellerChange}
          >
            <MenuItem value="">
              <em>All Sellers</em>
            </MenuItem>
            {sellers.map((s: any) => (
              <MenuItem key={s.id} value={s.id}>
                {s.sellerName || s.businessDetails?.businessName || `Seller #${s.id}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel id="main-category-label">Main Category</InputLabel>
          <Select
            labelId="main-category-label"
            name="mainCategory"
            value={formik.values.mainCategory}
            label="Main Category"
            onChange={handleMainCategoryChange}
          >
            <MenuItem value="">
              <em>All Main Categories</em>
            </MenuItem>
            {mainCategories.map((c) => (
              <MenuItem key={getId(c)} value={getId(c)}>
                {c.name || c.categoryName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" disabled={!subCategories.length}>
          <InputLabel id="sub-category-label">Sub-Category</InputLabel>
          <Select
            labelId="sub-category-label"
            name="subCategory"
            value={formik.values.subCategory}
            label="Sub-Category"
            onChange={handleSubCategoryChange}
          >
            <MenuItem value="">
              <em>All Sub-Categories</em>
            </MenuItem>
            {subCategories.map((sc) => (
              <MenuItem key={getId(sc)} value={getId(sc)}>
                {sc.name || sc.categoryName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" disabled={!itemCategories.length}>
          <InputLabel id="item-category-label">Item Category</InputLabel>
          <Select
            labelId="item-category-label"
            name="itemCategory"
            value={formik.values.itemCategory}
            label="Item Category"
            onChange={handleItemCategoryChange}
          >
            <MenuItem value="">
              <em>All Item Categories</em>
            </MenuItem>
            {itemCategories.map((ic) => (
              <MenuItem key={getId(ic)} value={getId(ic)}>
                {ic.name || ic.categoryName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel id="product-select-label">
            Select Product ({filteredProducts.length} available)
          </InputLabel>
          <Select
            labelId="product-select-label"
            name="productId"
            value={formik.values.productId}
            label={`Select Product (${filteredProducts.length} available)`}
            onChange={formik.handleChange}
          >
            {filteredProducts.length > 0 ? (
              filteredProducts.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.title} - (KES {p.sellingPrice ?? p.mrpPrice ?? 0})
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled value="">
                <em>No products available for selected filter</em>
              </MenuItem>
            )}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          size="small"
          type="date"
          name="validTill"
          label="Valid Till"
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: todayStr }}
          value={formik.values.validTill}
          onChange={formik.handleChange}
        />

        <Button
          fullWidth
          type="submit"
          variant="contained"
          color="primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Adding..." : "Add Sponsored Product"}
        </Button>
      </Box>

      <Divider />

      <h2 className="text-xl font-bold text-gray-800">Active Sponsored Products</h2>
      <TableContainer component={Paper} className="shadow-md">
        <Table>
          <TableHead className="bg-slate-100">
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Seller</TableCell>
              <TableCell>Valid Till</TableCell>
              <TableCell align="center">Active / Disable</TableCell>
              <TableCell align="center">Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sponsoredProducts.length > 0 ? (
              sponsoredProducts.map((item: any) => {
                const p: Product = item.product || item;
                const sponsoredRecordId: number = item.id; // SponsoredProduct primary key
                const imageUrl =
                  p.images?.[0] || p.image || "https://via.placeholder.com/150";

                const isCurrentlySponsored = item.active ?? p.sponsored ?? p.isSponsored ?? true;
                const validTillDate = item.validTill || p.sponsoredValidTill;

                return (
                  <TableRow key={sponsoredRecordId}>
                    <TableCell>
                      <img
                        src={imageUrl}
                        alt={p.title || "Sponsored Product"}
                        className="w-12 h-12 object-cover rounded"
                      />
                    </TableCell>
                    <TableCell>{p.title || "N/A"}</TableCell>
                    <TableCell>KES {p.sellingPrice ?? p.mrpPrice ?? 0}</TableCell>
                    <TableCell>
                      {p.seller?.businessDetails?.businessName ||
                        p.seller?.sellerName ||
                        "N/A"}
                    </TableCell>
                    <TableCell>
                      {validTillDate
                        ? new Date(validTillDate).toLocaleDateString()
                        : "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={Boolean(isCurrentlySponsored)}
                        onChange={() => handleToggleSponsor(sponsoredRecordId)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleDeleteSponsor(sponsoredRecordId)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" className="py-6 text-gray-500">
                  No sponsored products found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default SponsoredProductsTable;